'use client';

import { useState, useEffect } from 'react';
import type {
    Firestore,
    DocumentReference,
    CollectionReference,
    Query,
    DocumentData,
    Timestamp as FirestoreTimestampType,
    FieldValue as FirestoreFieldValType,
    WriteBatch,
    Transaction,
} from 'firebase/firestore';
// Import the promise for the Firestore service instance
import { firestore as firestoreServicePromise } from '../lib/firebaseFirestore';

// Define a type for the dynamically imported Firestore functions
interface FirestoreFunctions {
    doc: typeof import('firebase/firestore').doc;
    getDoc: typeof import('firebase/firestore').getDoc;
    setDoc: typeof import('firebase/firestore').setDoc;
    updateDoc: typeof import('firebase/firestore').updateDoc;
    deleteDoc: typeof import('firebase/firestore').deleteDoc;
    collection: typeof import('firebase/firestore').collection;
    query: typeof import('firebase/firestore').query;
    where: typeof import('firebase/firestore').where;
    getCountFromServer: typeof import('firebase/firestore').getCountFromServer;
    onSnapshot: typeof import('firebase/firestore').onSnapshot;
    writeBatch: typeof import('firebase/firestore').writeBatch;
    serverTimestamp: typeof import('firebase/firestore').serverTimestamp;
    Timestamp: typeof import('firebase/firestore').Timestamp; // Class for creating Timestamps
    FieldValue: typeof import('firebase/firestore').FieldValue; // Class for FieldValues
    runTransaction: typeof import('firebase/firestore').runTransaction;
    getDocs: typeof import('firebase/firestore').getDocs;
    orderBy: typeof import('firebase/firestore').orderBy;
    limit: typeof import('firebase/firestore').limit;
    startAfter: typeof import('firebase/firestore').startAfter;
}

// Import firestore functions directly, they will operate on the db instance from initializeFirebaseCore
import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    collection,
    query,
    where,
    getCountFromServer,
    onSnapshot,
    writeBatch,
    serverTimestamp,
    Timestamp,
    FieldValue,
    runTransaction,
    getDocs,
    orderBy,
    limit,
    startAfter,
} from 'firebase/firestore';

export function useFirestore() {
    const [db, setDb] = useState<Firestore | null>(null);
    const [firestoreFuncs] = useState<FirestoreFunctions | null>({
        doc,
        getDoc,
        setDoc,
        updateDoc,
        deleteDoc,
        collection,
        query,
        where,
        getCountFromServer,
        onSnapshot,
        writeBatch,
        serverTimestamp,
        Timestamp,
        FieldValue,
        runTransaction,
        getDocs,
        orderBy,
        limit,
        startAfter,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        console.log('[useFirestore] useEffect triggered.'); // Log when effect runs
        if (typeof window === 'undefined') {
            console.warn(
                '[useFirestore] Attempted to use useFirestore hook in server-side context. This hook is client-side only.'
            );
            setIsLoading(false);
            setError(new Error('Cannot use Firestore in server-side context'));
            console.log('[useFirestore] Set error: SSR, isLoading: false');
            return;
        }

        let isMounted = true;
        console.log(
            '[useFirestore] Initializing: setIsLoading(true), setError(null)'
        );
        setIsLoading(true);
        setError(null);

        firestoreServicePromise
            .then((instance) => {
                console.log(
                    '[useFirestore] firestoreServicePromise.then called.'
                );
                if (isMounted) {
                    if (instance) {
                        setDb(instance);
                        console.info(
                            '[useFirestore] Firestore instance obtained. setDb(instance), setIsLoading(false). Current db:',
                            instance
                        );
                    } else {
                        const err = new Error(
                            '[useFirestore] Firestore instance was not available from promise.'
                        );
                        setError(err);
                        console.error(
                            '[useFirestore] Firestore instance was null. setError(err), setIsLoading(false). Error:',
                            err
                        );
                    }
                    setIsLoading(false);
                }
            })
            .catch((err) => {
                console.log(
                    '[useFirestore] firestoreServicePromise.catch called.'
                );
                if (isMounted) {
                    console.error(
                        '[useFirestore] Error obtaining Firestore instance via promise. setError(err), setIsLoading(false). Error:',
                        err
                    );
                    setError(err as Error);
                    setIsLoading(false);
                }
            });

        return () => {
            console.log('[useFirestore] useEffect cleanup. isMounted = false.');
            isMounted = false;
        };
    }, []);

    // Log state changes for db, isLoading, and error
    useEffect(() => {
        console.log(
            '[useFirestore] State changed: db:',
            db,
            'isLoading:',
            isLoading,
            'error:',
            error
        );
    }, [db, isLoading, error]);

    return { db, firestoreFuncs, isLoading, error };
}

// Export common types that might be needed by components using this hook
export type {
    DocumentReference,
    CollectionReference,
    Query,
    DocumentData,
    FirestoreTimestampType,
    FirestoreFieldValType,
    WriteBatch,
    Transaction,
};
